import type { Cores, Esquema } from './tokens';

// Direção "Rua": alto contraste, feita para ler no sol e na moto. Os apps (useTema) e o painel web
// (cssDoTema, em ./css.ts) usam estes mesmos tokens.
//
// A cor de ação é o limão. No escuro ela é o fundo (texto preto por cima); no claro o limão
// sumiria no branco, então a ação vira tinta (preto) com o limão por cima. Trilho de arrastar,
// botão principal e aba ativa usam o mesmo par `acao` / `textoSobreAcao` e invertem sozinhos.
//
// Marca (opção A, 22/10/2026): o selo do logo usa esse mesmo par. `marca` é o fundo do selo e
// `textoSobreMarca` o cubo dentro dele; `marcaTexto` é o texto de destaque (tinta no claro, limão no escuro).

export const LIMAO = '#D4FF3A';
export const TINTA = '#0B0B0E';

export interface CoresDoApp extends Cores {
  /** Preenchimento da ação principal (botão, trilho de arrastar, aba ativa). */
  acao: string;
  acaoPressionada: string;
  /** Texto e ícone sobre `acao` (e a alça do trilho). */
  textoSobreAcao: string;
  /** Limão nos dois esquemas, só como preenchimento (gráfico, ponto). Nunca texto sobre o claro. */
  destaque: string;
  /** Texto e ícone sobre o limão (botão principal): tinta nos dois esquemas. */
  textoSobreDestaque: string;
  /** Contorno do campo/caixa em foco: limão no escuro, tinta no claro. */
  contornoAtivo: string;
  /** Cartão de vidro sobre imagem (fundo translúcido + borda). */
  vidro: { fundo: string; borda: string };
  /** Luz do reflexo que atravessa o botão principal e o trilho (branca nos dois esquemas). */
  reflexo: string;
  /** Mapa ilustrado das telas de entrada. */
  mapa: { fundo: string; rua: string; ruaFina: string; quadra: string; rota: string };
}

const claro: CoresDoApp = {
  fundo: '#FFFFFF',
  fundoSutil: '#F2F2F5',
  superficie: '#F2F2F5',
  superficieElevada: '#E7E7EC',
  superficieSelecionada: '#E7E7EC',
  borda: '#D4D4DB',
  bordaForte: '#A1A1AA',
  estrela: '#F59E0B',
  divisor: '#E4E4E9',
  texto: TINTA,
  textoSuave: '#52525B',
  textoFraco: '#5F5F68',
  textoDesabilitado: '#A1A1AA',
  textoSobreSolido: '#FFFFFF',
  marca: TINTA,
  textoSobreMarca: LIMAO,
  marcaTexto: TINTA,
  foco: 'rgba(11, 11, 14, 0.35)',
  campoFundo: '#FFFFFF',
  campoBorda: '#84848C',
  campoBordaHover: '#52525B',
  sobreposicao: 'rgba(11, 11, 14, 0.4)',
  fundoContraste: TINTA,
  textoContraste: '#FFFFFF',
  acao: TINTA,
  acaoPressionada: '#2A2A31',
  textoSobreAcao: LIMAO,
  destaque: LIMAO,
  textoSobreDestaque: TINTA,
  contornoAtivo: TINTA,
  vidro: { fundo: 'rgba(255, 255, 255, 0.8)', borda: 'rgba(11, 11, 14, 0.08)' },
  reflexo: '#FFFFFF',
  mapa: { fundo: '#E6E7EC', rua: '#FFFFFF', ruaFina: '#F4F4F7', quadra: '#DCDDE3', rota: TINTA },
  tons: {
    neutro: { fundo: '#E7E7EC', texto: '#3F3F46', solido: '#3F3F46' },
    andamento: { fundo: '#DBEAFE', texto: '#1D4ED8', solido: '#1D4ED8' },
    sucesso: { fundo: '#DCFCE7', texto: '#166534', solido: '#15803D' },
    alerta: { fundo: '#FEF3C7', texto: '#92400E', solido: '#B45309' },
    erro: { fundo: '#FEE2E2', texto: '#B91C1C', solido: '#B91C1C' },
  },
};

const escuro: CoresDoApp = {
  fundo: TINTA,
  fundoSutil: '#131318',
  superficie: '#17171C',
  superficieElevada: '#23232B',
  superficieSelecionada: '#23232B',
  borda: '#2A2A33',
  bordaForte: '#3F3F46',
  estrela: '#FBBF24',
  divisor: '#1F1F26',
  texto: '#F5F5F7',
  textoSuave: '#A1A1AA',
  textoFraco: '#8E8E98',
  textoDesabilitado: '#52525B',
  textoSobreSolido: '#FFFFFF',
  marca: LIMAO,
  textoSobreMarca: TINTA,
  marcaTexto: LIMAO,
  foco: 'rgba(212, 255, 58, 0.45)',
  campoFundo: '#101014',
  campoBorda: '#6B6B74',
  campoBordaHover: '#A1A1AA',
  sobreposicao: 'rgba(0, 0, 0, 0.7)',
  fundoContraste: '#23232B',
  textoContraste: '#F5F5F7',
  acao: LIMAO,
  acaoPressionada: '#BCE62A',
  textoSobreAcao: TINTA,
  destaque: LIMAO,
  textoSobreDestaque: TINTA,
  contornoAtivo: LIMAO,
  vidro: { fundo: 'rgba(23, 23, 28, 0.78)', borda: 'rgba(255, 255, 255, 0.08)' },
  reflexo: '#FFFFFF',
  mapa: { fundo: '#0F1016', rua: '#1C1D26', ruaFina: '#16171F', quadra: '#13141B', rota: LIMAO },
  tons: {
    neutro: { fundo: '#23232B', texto: '#A1A1AA', solido: '#3F3F46' },
    andamento: { fundo: '#14213A', texto: '#7DB4FF', solido: '#2563EB' },
    sucesso: { fundo: '#12281A', texto: '#4ADE80', solido: '#15803D' },
    alerta: { fundo: '#2A230F', texto: '#FBBF24', solido: '#B45309' },
    erro: { fundo: '#2E1515', texto: '#F87171', solido: '#B91C1C' },
  },
};

export const temasDoApp: Record<Esquema, CoresDoApp> = { claro, escuro };

/**
 * Inter por peso (estilo do GravaAí: títulos 700, texto 400/500, nada em caixa alta).
 * No React Native cada peso é uma família carregada à parte.
 */
export const fonteDoApp = {
  '400': 'Inter_400Regular',
  '500': 'Inter_500Medium',
  '600': 'Inter_600SemiBold',
  '700': 'Inter_700Bold',
} as const;
export type PesoDoApp = keyof typeof fonteDoApp;
